/**
 * InlineFeld — ein Feld, das an Ort und Stelle bearbeitet wird (#158, Staffing-Maske).
 *
 * Speichert beim Verlassen (blur) oder mit Enter (`textfeld`: Strg+Enter, weil Enter dort
 * eine neue Zeile ist; `auswahl`: sofort bei der Wahl). Escape verwirft den Entwurf. Ein
 * unveraenderter Wert loest keinen Aufruf aus. Scheitert `onSpeichern`, steht wieder der
 * alte Wert im Feld und ein Toast meldet den Fehler. Ohne `kannBearbeiten` nur Anzeige.
 *
 * Zahl: leere Eingabe heisst `null`; Nicht-Zahlen werden nicht gespeichert (alter Wert
 * bleibt, kein Aufruf).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../toast.js';

export type InlineFeldArt = 'text' | 'zahl' | 'auswahl' | 'textfeld';

export type InlineFeldProps<T extends string | number> = {
  wert: T | null;
  art: InlineFeldArt;
  optionen?: { value: string; label: string }[];
  kannBearbeiten: boolean;
  onSpeichern: (neu: T | null) => Promise<void>;
  label: string;
  platzhalter?: string;
  /**
   * Nur `textfeld`: Zeilenzahl je nach Entwurf (leer/gefüllt), Standard 3. Mehr Text scrollt im
   * Feld (Übergabe-Notiz, #158).
   */
  zeilen?: { leer: number; gefuellt: number };
};

/**
 * Rahmen jedes bearbeitbaren Feldes der Staffing-Maske (#158 F2): im Bearbeitungsmodus immer ein
 * leichter Rahmen (Token `--line`), mit Fokus kräftiger (`--primary`). Der Lesemodus hat keinen.
 * Gilt auch für die Selects/Inputs der Maske außerhalb von `InlineFeld`.
 */
export const EINGABE_RAHMEN =
  'border border-[color:var(--line)] focus:border-[color:var(--primary)] focus:outline-none';

const FELD_KLASSE = `w-full rounded ${EINGABE_RAHMEN} bg-transparent px-1 py-0.5 text-sm focus:bg-[var(--surface)] disabled:opacity-60`;

/** Eingabetext -> Wert. `undefined` = ungueltig (nicht speichern), `null` = leer. */
function parseEntwurf(art: InlineFeldArt, entwurf: string): string | number | null | undefined {
  if (art === 'zahl') {
    const roh = entwurf.trim().replace(',', '.');
    if (roh === '') return null;
    const n = Number(roh);
    return Number.isFinite(n) ? n : undefined;
  }
  if (art === 'auswahl') return entwurf === '' ? null : entwurf;
  return entwurf.trim() === '' ? null : entwurf;
}

export function InlineFeld<T extends string | number>({
  wert,
  art,
  optionen,
  kannBearbeiten,
  onSpeichern,
  label,
  platzhalter,
  zeilen,
}: InlineFeldProps<T>) {
  const { t } = useTranslation();
  const toast = useToast();
  const gespeichert = wert === null || wert === undefined ? '' : String(wert);
  const [entwurf, setEntwurf] = useState(gespeichert);
  const [laeuft, setLaeuft] = useState(false);
  const feldRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);
  // Zuletzt abgeschickter Entwurf: Enter + anschliessendes blur schreibt nicht doppelt.
  const zuletzt = useRef<string | null>(null);

  // Neuer Server-Wert uebernimmt den Entwurf, ausser jemand tippt gerade.
  useEffect(() => {
    zuletzt.current = null;
    if (document.activeElement !== feldRef.current) setEntwurf(gespeichert);
  }, [gespeichert]);

  const speichern = async () => {
    if (laeuft || entwurf === gespeichert || entwurf === zuletzt.current) return;
    const neu = parseEntwurf(art, entwurf);
    if (neu === undefined) {
      setEntwurf(gespeichert);
      return;
    }
    // Leer -> null: war schon leer, nichts zu tun.
    if (neu === null && gespeichert === '') {
      setEntwurf(gespeichert);
      return;
    }
    zuletzt.current = entwurf;
    setLaeuft(true);
    try {
      await onSpeichern(neu as T | null);
    } catch (e) {
      zuletzt.current = null;
      setEntwurf(gespeichert);
      toast.error(
        t('v2.inline_feld.speichern_fehlgeschlagen', 'Speichern fehlgeschlagen'),
        e instanceof Error ? e.message : undefined,
      );
    } finally {
      setLaeuft(false);
    }
  };

  if (!kannBearbeiten) {
    const anzeige =
      art === 'auswahl'
        ? (optionen?.find((o) => o.value === gespeichert)?.label ?? gespeichert)
        : gespeichert;
    return (
      <span className="whitespace-pre-wrap text-sm" title={label}>
        {anzeige === '' ? (platzhalter ?? t('v2.inline_feld.leer', 'Keine Angabe')) : anzeige}
      </span>
    );
  }

  if (art === 'auswahl') {
    return (
      <select
        ref={feldRef as React.RefObject<HTMLSelectElement>}
        aria-label={label}
        className={FELD_KLASSE}
        value={entwurf}
        disabled={laeuft}
        onChange={(e) => {
          const v = e.target.value;
          setEntwurf(v);
          if (v === gespeichert) return;
          zuletzt.current = v;
          setLaeuft(true);
          // Über `Promise.resolve().then` landet auch ein synchroner Wurf in `onSpeichern`
          // (z. B. Pflichtprüfung) im `.catch` — sonst bliebe die Auswahl dauerhaft gesperrt.
          Promise.resolve()
            .then(() => onSpeichern((v === '' ? null : v) as T | null))
            .catch((err: unknown) => {
              zuletzt.current = null;
              setEntwurf(gespeichert);
              toast.error(
                t('v2.inline_feld.speichern_fehlgeschlagen', 'Speichern fehlgeschlagen'),
                err instanceof Error ? err.message : undefined,
              );
            })
            .finally(() => setLaeuft(false));
        }}
      >
        <option value="">
          {platzhalter ?? t('v2.inline_feld.keine_auswahl', 'Keine Auswahl')}
        </option>
        {(optionen ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  if (art === 'textfeld') {
    return (
      <textarea
        ref={feldRef as React.RefObject<HTMLTextAreaElement>}
        aria-label={label}
        placeholder={platzhalter}
        className={zeilen ? `${FELD_KLASSE} resize-none overflow-y-auto` : FELD_KLASSE}
        rows={zeilen ? (entwurf === '' ? zeilen.leer : zeilen.gefuellt) : 3}
        value={entwurf}
        disabled={laeuft}
        onChange={(e) => setEntwurf(e.target.value)}
        onBlur={() => void speichern()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setEntwurf(gespeichert);
          else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void speichern();
          }
        }}
      />
    );
  }

  return (
    <input
      ref={feldRef as React.RefObject<HTMLInputElement>}
      type="text"
      inputMode={art === 'zahl' ? 'decimal' : undefined}
      aria-label={label}
      placeholder={platzhalter}
      className={FELD_KLASSE}
      value={entwurf}
      disabled={laeuft}
      onChange={(e) => setEntwurf(e.target.value)}
      onBlur={() => void speichern()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setEntwurf(gespeichert);
        else if (e.key === 'Enter') {
          e.preventDefault();
          void speichern();
        }
      }}
    />
  );
}
